/**
 * FRONT-2550 — the decision logic of removing a tag from an open item, kept
 * out of the component so it is testable without a DOM (the frontend suite
 * runs under plain node and never runs effects or clicks).
 *
 * The rule, in one line: never show a state that is not true. The chip hides
 * the moment the person clicks (so the click feels answered), and ONLY a
 * failed save brings it back — with the error told — because the list the
 * server holds still has it. A successful save keeps it hidden: the server
 * list no longer has it either, so there is nothing to restore, and the
 * refetch that follows is best-effort polish, never a precondition.
 */

/** The tags to render: everything the item carries except chips being removed. */
export function visibleTags(tags: readonly string[], removing: ReadonlySet<string>): string[] {
  return tags.filter((t) => !removing.has(t));
}

export interface TagRemovalDeps {
  tag: string;
  /** The COMPLETE list to save: what is visible now, minus `tag`. */
  remaining: string[];
  hide: (tag: string) => void;
  restore: (tag: string) => void;
  save: (tags: string[]) => Promise<unknown>;
  /** The in-flight flag the × buttons disable on: true while the save is
   *  pending, false again once it settled either way. It is deliberately NOT
   *  derived from the hidden set, which keeps a removed tag after success. */
  setBusy: (busy: boolean) => void;
  onSuccess: () => void;
  onError: (err: unknown) => void;
}

export async function removeTagWithRestore(d: TagRemovalDeps): Promise<void> {
  d.hide(d.tag);
  d.setBusy(true);
  try {
    await d.save(d.remaining);
  } catch (err) {
    d.restore(d.tag);
    d.onError(err);
    return;
  } finally {
    d.setBusy(false);
  }
  d.onSuccess();
}

/**
 * Where keyboard focus goes after `tag` is removed from `shown` (the visible
 * list, before the removal): the next chip, else the previous one, else null
 * (no chip left — the caller focuses the row's label). Returns the tag to
 * focus, so the caller can find that chip's × button.
 */
export function focusTargetAfterRemoval(shown: readonly string[], tag: string): string | null {
  const i = shown.indexOf(tag);
  if (i === -1) return null;
  return shown[i + 1] ?? shown[i - 1] ?? null;
}
